import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import gameService from '../../services/gameService';
import Modal from '../../components/Modal';
import Pagination from '../../components/Pagination';
import { SearchIcon, EditIcon, TrashIcon } from '../../components/Icons';

const GameCountries = () => {
  const { hasPermission } = useAuth();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination & Search State
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Modal & Form State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    is_active: true
  });

  const fetchData = useCallback(async (page = pagination.page, search = searchTerm, limit = pagination.limit) => {
    try {
      setLoading(true);
      const res = await gameService.getCountries({ page, limit, search });

      if (res.success) {
        setCountries(res.data);
        setPagination(prev => ({
          ...prev,
          page: res.pagination.page,
          total: res.pagination.total,
          pages: res.pagination.pages
        }));
      }
    } catch (err) {
      setError('An error occurred while fetching data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.page, searchTerm]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchData(1, searchTerm);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const canEdit = hasPermission('countries', 'edit');
  const canDelete = hasPermission('countries', 'delete');
  const showActions = canEdit || canDelete;

  const handlePageChange = (newPage) => {
    fetchData(newPage, searchTerm);
  };

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({ ...prev, limit: newLimit }));
    fetchData(1, searchTerm, newLimit);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const openAddModal = () => {
    setEditId(null);
    setFormData({ code: '', name: '', is_active: true });
    setIsModalOpen(true);
  };

  const openEditModal = (country) => {
    setEditId(country._id);
    setFormData({
      code: country.code,
      name: country.name,
      is_active: country.is_active
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      let response;
      if (editId) {
        response = await gameService.updateCountry(editId, formData);
      } else {
        response = await gameService.createCountry(formData);
      }

      if (response.success) {
        setIsModalOpen(false);
        fetchData();
      } else {
        alert(response.message || 'Failed to save country');
      }
    } catch (err) {
      // Axios puts server error responses in err.response.data
      const serverMsg = err?.response?.data?.message;
      alert(serverMsg || err?.message || 'An error occurred while saving country');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this country? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await gameService.deleteCountry(id);
      if (response.success) {
        fetchData();
      } else {
        alert(response.message || 'Failed to delete country');
        setLoading(false);
      }
    } catch (err) {
      const serverMsg = err?.response?.data?.message;
      alert(serverMsg || err?.message || 'An error occurred while deleting country');
      setLoading(false);
    }
  };

  return (
    <div className="game-countries-page">
      {hasPermission('countries', 'create') && (
        <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={openAddModal}>Add Country</button>
        </div>
      )}

      <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
        <div className="search-container">
          <div className="search-icon-wrapper">
            <SearchIcon />
          </div>
          <input
            type="text"
            className="search-input"
            placeholder="Search code or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading && countries.length === 0 ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
      ) : error ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--error)' }}>{error}</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <table className="table-striped" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--table-header-bg)', borderBottom: '1px solid var(--border-light)' }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Code</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Name</th>
                 <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Status</th>
                 {showActions && <th style={{ padding: '1rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Actions</th>}
               </tr>
            </thead>
            <tbody style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
              {countries.length === 0 ? (
                <tr>
                  <td colSpan="4" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No countries found.</td>
                </tr>
              ) : (
                countries.map((country) => (
                  <tr key={country._id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', fontWeight: 600 }}>{country.code}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{country.name}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      <span style={{
                        padding: '0.25rem 0.625rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        backgroundColor: country.is_active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                        color: country.is_active ? 'var(--success)' : 'var(--error)'
                      }}>
                        {country.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                     {showActions && (
                      <td style={{ padding: '1rem', textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                        {canEdit && (
                          <button
                            className="btn"
                            style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                            onClick={() => openEditModal(country)}
                          >
                            <EditIcon size={14} />
                            Edit
                          </button>
                        )}
                        {canDelete && (
                          <button
                            className="btn"
                            style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', color: 'var(--error)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                            onClick={() => handleDelete(country._id)}
                          >
                            <TrashIcon size={14} />
                            Delete
                          </button>
                        )}
                      </td>
                     )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          pagination={pagination}
          onPageChange={(pg) => fetchData(pg)}
          onLimitChange={(l) => {
            setPagination(prev => ({ ...prev, limit: l }));
            fetchData(1, searchTerm, l);
          }}
          loading={loading}
          itemName="countries"
        />
      </div>
      )}

      {/* Add/Edit Country Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editId ? "Edit Country" : "Add New Country"}
      >
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Country Code</label>
            <input
              type="text"
              name="code"
              className="input"
              value={formData.code}
              onChange={handleInputChange}
              required
              placeholder="e.g. US, MY, TH"
            />
          </div>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Country Name</label>
            <input
              type="text"
              name="name"
              className="input"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="e.g. United States, Malaysia, Thailand"
            />
          </div>
          <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Is Active</span>
            <label className="switch">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
              />
              <span className="slider"></span>
            </label>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border-light)' }}>
            <button type="button" className="btn" onClick={() => setIsModalOpen(false)} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : (editId ? 'Update Country' : 'Create Country')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default GameCountries;
